import { useCallback, useEffect, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import ReactMarkdown from "react-markdown";

import type { Editor } from "tinymce";

import { useAppDispatch } from "app/hooks";
import {
  setCustomMessage,
  setOpen as setSnackbarOpen
} from "components/action-snackbar/action-snackbar-slice";
import useAudiologyReportSummaryNote from "features/medical-scribe/hooks/use-audiology-report-summary-note.hook";
import type { MedicalScribeContextType } from "features/medical-scribe/medical-scribe-context-type.type";
import type { AmbientScribe } from "services/types/ambient-scribe.type";
import { isLikelyHtml } from "utils/markdown.utils";

import useCopyHtmlContentToClipboard from "../hooks/use-copy-html-content-to-clipboard.hook";
import {
  getInsertButtonLabel,
  getScribeInsertAction,
  getScribeInsertSourceText,
  getUsableEditor,
  hasInsertableContent,
  isMarkdownConvertibleTab,
  placeInsertCaret
} from "./utils";

type InsertScribeSubmitParams = Pick<
  MedicalScribeContextType,
  "editorRef" | "insertScribeSelectionBookmarkRef"
> & {
  tabView: string;
  /** What the details show: the source of the text Insert writes. */
  effectiveScribe: AmbientScribe | null;
  selectedScribeId: AmbientScribe["id"] | undefined;
  isAudiologyMode: boolean;
  audiologyReportId: number | undefined;
  onClose: () => void;
};

/**
 * What Insert writes, where it writes it, and what the button says.
 */
export default function useInsertScribeSubmit({
  editorRef,
  insertScribeSelectionBookmarkRef,
  tabView,
  effectiveScribe,
  selectedScribeId,
  isAudiologyMode,
  audiologyReportId,
  onClose
}: InsertScribeSubmitParams) {
  const dispatch = useAppDispatch();

  /**
   * The editor Insert will write into, captured ONCE when the dialog opens.
   *
   * State, not a live `editorRef.current` read: a ref mutation does not re-render, so the button
   * label, its icon and the "Replace existing content" checkbox could not observe the ref changing
   * and would promise "Insert" while the handler silently copied to the clipboard instead. Reading
   * one snapshot for both the labels and the action makes them agree by construction.
   *
   * The dialog only mounts while `show` is true, so mount is open. Callers claim the ref
   * immediately before dispatching `showInsertScribe`, which is what makes this snapshot the right
   * editor rather than whichever one initialised last.
   */
  const [targetEditor, setTargetEditor] = useState<Editor | null>(null);
  useEffect(() => {
    setTargetEditor(getUsableEditor(editorRef?.current));
  }, [editorRef]);

  // Same args as AudiologySummaryTab (both go through this hook), so RTK Query serves one
  // request and the button can never insert something different from what is on screen.
  const { latestSucceeded: audiologySummaryNote } =
    useAudiologyReportSummaryNote({
      scribeId: selectedScribeId,
      reportId: audiologyReportId,
      enabled: isAudiologyMode
    });
  const audiologyNoteHtml = audiologySummaryNote?.notes;

  const getText = useCallback(() => {
    const text = getScribeInsertSourceText({
      tabView,
      scribe: effectiveScribe,
      isAudiologyMode,
      audiologyNoteHtml
    });
    if (text && isMarkdownConvertibleTab(tabView) && !isLikelyHtml(text)) {
      return renderToStaticMarkup(<ReactMarkdown>{text}</ReactMarkdown>);
    }
    return text;
  }, [effectiveScribe, tabView, isAudiologyMode, audiologyNoteHtml]);

  const handleCopyText = useCopyHtmlContentToClipboard();

  const copyAndClose = useCallback(async () => {
    const text = getText();
    await handleCopyText(text, true);
    onClose();
  }, [getText, handleCopyText, onClose]);

  const insert = useCallback(
    async (needReplace: boolean) => {
      const text = getText();

      const action = getScribeInsertAction({
        hasEditor: Boolean(targetEditor),
        needReplace,
        text
      });

      if (action === "copy") {
        // There is no editor to write into. Say so — this used to be indistinguishable from a
        // successful insert, which is exactly how a broken insert went unnoticed. The hook
        // reports its own failure, so only claim the clipboard when the write succeeded.
        const copied = await handleCopyText(text);
        if (copied) {
          dispatch(
            setCustomMessage(
              "The editor isn't available, so the scribe content was copied to your clipboard instead."
            )
          );
          dispatch(setSnackbarOpen(5000));
        }
      } else if (action === "replace") {
        insertScribeSelectionBookmarkRef.current = null;
        targetEditor?.setContent(text);
      } else if (action === "insert" && targetEditor) {
        // Openers write this as they dispatch: a bookmark when their editor holds the user's
        // caret, `null` when it does not. Always fresh for the surface that opened the dialog.
        const bookmark = insertScribeSelectionBookmarkRef.current;
        insertScribeSelectionBookmarkRef.current = null;

        targetEditor.focus();
        placeInsertCaret(targetEditor, bookmark);
        targetEditor.insertContent(text);
      }
      // action === "noop": empty replace — leave the chart note untouched.

      onClose();
    },
    [
      getText,
      targetEditor,
      insertScribeSelectionBookmarkRef,
      onClose,
      handleCopyText,
      dispatch
    ]
  );

  return {
    hasEditor: Boolean(targetEditor),
    hasContent: hasInsertableContent({
      tabView,
      scribe: effectiveScribe,
      isAudiologyMode,
      audiologyNoteHtml
    }),
    buttonLabel: getInsertButtonLabel({
      tabView,
      hasEditor: Boolean(targetEditor),
      isAudiologyMode
    }),
    insert,
    copyAndClose
  };
}
